import {
  AllowedFilesEntities,
  Building,
  ChargesMap,
  Contact,
  ContactType,
  CustomLabel,
  DocumentCategoryWithoutReadIdAndClientId,
  EntityType,
  FileCategory,
  File as FileModel,
  FolderEntity,
  Invoice,
  Language,
  ROOT_FOLDER_ID,
  Technic,
  Unit,
  UnitLease,
  allLanguageMessages,
  contactMostImportantType,
  createDeterministicFolderId,
  fromDateTime,
  getContactNameFromObject,
  isGeneratedUblDocument,
  isNilOrEmpty,
  todayDateTime,
} from '@rentguru/commons-utils';
import { isNil } from 'lodash';

import { type FolderDatabaseModel } from 'src/hooks/Folderhook/folderContextUtils';

export const CONTACT_ENTITY_ID = 'contacts';

const allowedContactTypes = [
  ContactType.CONTRACTOR,
  ContactType.GUARANTOR,
  ContactType.JOINT_OWNERSHIP,
  ContactType.OWNER,
  ContactType.TENANT,
];

export const syncContacts = (contacts: Contact[], allFolders: FolderDatabaseModel[]): FolderDatabaseModel[] => {
  const foldersToAdd: FolderDatabaseModel[] = [];
  let rootFolderContact = allFolders.find(
    (folder) => folder.folderEntity === FolderEntity.CONTACT && folder.depth === ROOT_FOLDER_ID
  );
  if (!rootFolderContact) {
    const rootContactFolderId = createDeterministicFolderId(FolderEntity.CONTACT, CONTACT_ENTITY_ID);
    const labels = Object.entries(allLanguageMessages).map(([lang, translations]) => ({
      language: lang as Language,
      label: translations.settings.rolesAndPermissions.sections.contacts,
    }));

    const rootContactFolder: FolderDatabaseModel = {
      id: rootContactFolderId,
      depth: ROOT_FOLDER_ID,
      depthByFolderId: [],
      depthString: [],
      folderEntity: FolderEntity.CONTACT,
      entityId: CONTACT_ENTITY_ID,
      documentCategoryId: '',
      categoryType: FileCategory.CUSTOM,
      categoryEntity: EntityType.CONTACT,
      labels,
    };
    rootFolderContact = rootContactFolder;
    foldersToAdd.push(rootContactFolder);
  }
  const contactFolders = contacts.reduce<FolderDatabaseModel[]>((acc, contact) => {
    if (!contact.types.some((contactType) => allowedContactTypes.includes(contactType))) {
      return acc;
    }
    const existingContactFolder = allFolders.find(
      (folder) => folder.entityId === contact.id && folder.folderEntity === FolderEntity.CONTACT
    );
    if (existingContactFolder) {
      //Needs to be updated if contact name changes
      return acc;
    }
    const newContactFolderId = createDeterministicFolderId(FolderEntity.CONTACT, contact.id);
    const newContactFolder: FolderDatabaseModel = {
      id: newContactFolderId,
      depth: `${ROOT_FOLDER_ID}.contacts`,
      depthByFolderId: [rootFolderContact.id, newContactFolderId],
      depthString: [rootFolderContact.labels!],
      translatedLabel: getContactNameFromObject(contact),
      folderEntity: FolderEntity.CONTACT,
      entityId: contact.id,
      documentCategoryId: '',
      categoryType: FileCategory.CUSTOM,
      categoryEntity: EntityType.CONTACT,
    };
    if (contactMostImportantType(contact) === ContactType.JOINT_OWNERSHIP) {
      const jointOwnerShipFolders = calculateJointOwnershipFolders(contact, contacts, newContactFolder);
      foldersToAdd.push(...(jointOwnerShipFolders ?? []));
    }
    acc.push(newContactFolder);
    return acc;
  }, []);
  foldersToAdd.push(...contactFolders);

  return foldersToAdd;
};

const calculateJointOwnershipFolders = (contact: Contact, contacts: Contact[], contactFolder: FolderDatabaseModel) => {
  const jointOwnersContacts = contact.jointOwners?.map((jointOwner) => {
    return contacts.find((c) => c.id === jointOwner.contactId);
  });

  if (isNilOrEmpty(jointOwnersContacts)) {
    return;
  }
  const foldersToAdd = jointOwnersContacts.reduce<FolderDatabaseModel[]>((acc, jointOwnerContact) => {
    const jointOwnerFolderId = createDeterministicFolderId(FolderEntity.JOINT_CONTACT, jointOwnerContact!.id);
    const newContactFolder: FolderDatabaseModel = {
      id: jointOwnerFolderId,
      depth: `${contactFolder.depth}.${contactFolder.entityId}`,
      depthByFolderId: [...contactFolder.depthByFolderId, jointOwnerFolderId],
      depthString: [...contactFolder.depthString, contactFolder.translatedLabel!],
      translatedLabel: getContactNameFromObject(jointOwnerContact),
      folderEntity: FolderEntity.JOINT_CONTACT,
      entityId: jointOwnerContact!.id,
      documentCategoryId: '',
      categoryType: FileCategory.CUSTOM,
      categoryEntity: EntityType.CONTACT,
    };
    acc.push(newContactFolder);
    return acc;
  }, []);
  return foldersToAdd;
};

export const syncBuilding = (buildings: Building[], allFolders: FolderDatabaseModel[]): FolderDatabaseModel[] => {
  return buildings.reduce<FolderDatabaseModel[]>((acc, building) => {
    const existingBuildingFolder = allFolders.find(
      (folder) => folder.entityId === building.id && folder.folderEntity === FolderEntity.BUILDING
    );
    if (existingBuildingFolder) {
      //Needs to be updated if building name changes
      return acc;
    }
    const buildingFolderId = createDeterministicFolderId(FolderEntity.BUILDING, building.id);
    const newBuildingFolder = {
      id: buildingFolderId,
      depth: ROOT_FOLDER_ID,
      depthByFolderId: [buildingFolderId],
      depthString: [],
      translatedLabel: building.name,
      folderEntity: FolderEntity.BUILDING,
      entityId: building.id,
      documentCategoryId: '',
      categoryType: FileCategory.CUSTOM,
      categoryEntity: EntityType.BUILDING,
    };
    acc.push(newBuildingFolder);
    return acc;
  }, []);
};

export const syncUnits = (units: Unit[], allFolders: FolderDatabaseModel[]): FolderDatabaseModel[] => {
  return units.reduce<FolderDatabaseModel[]>((acc, unit) => {
    const existingUnitFolder = allFolders.find(
      (folder) => folder.entityId === unit.id && folder.folderEntity === FolderEntity.UNIT
    );
    if (existingUnitFolder) {
      //Needs to be updated if unit name changes
      return acc;
    }
    const buildingFolder = allFolders.find(
      (folder) => folder.entityId === unit.buildingId && folder.folderEntity === FolderEntity.BUILDING
    );
    if (isNil(buildingFolder)) {
      return acc;
    }

    const unitFolderId = createDeterministicFolderId(FolderEntity.UNIT, unit.id);
    const unitFolder: FolderDatabaseModel = {
      id: unitFolderId,
      depth: `${ROOT_FOLDER_ID}.${unit.buildingId}`,
      depthByFolderId: [buildingFolder.id, unitFolderId],
      translatedLabel: unit.name,
      folderEntity: FolderEntity.UNIT,
      size: 0,
      updatedAt: '',
      entityId: unit.id,
      documentCategoryId: '',
      categoryType: FileCategory.CUSTOM,
      categoryEntity: EntityType.UNIT,
      depthString: [unit.building?.name ?? ''],
    };
    acc.push(unitFolder);

    return acc;
  }, []);
};

export const syncLeases = (leases: UnitLease[], allFolders: FolderDatabaseModel[]): FolderDatabaseModel[] => {
  return leases.reduce<FolderDatabaseModel[]>((acc, lease) => {
    const existingLeaseFolder = allFolders.find(
      (folder) => folder.entityId === lease.leaseId && folder.folderEntity === FolderEntity.LEASE
    );
    if (existingLeaseFolder) {
      //Needs to be updated if lease name changes
      return acc;
    }
    const unitFolder = allFolders.find(
      (folder) => folder.entityId === lease.unitId && folder.folderEntity === FolderEntity.UNIT
    );
    if (isNil(unitFolder)) {
      return acc;
    }
    const leaseFolderId = createDeterministicFolderId(FolderEntity.LEASE, lease.leaseId);
    const leaseFolder: FolderDatabaseModel = {
      id: leaseFolderId,
      depth: getFolderDepthForNavigation(unitFolder),
      depthByFolderId: [...unitFolder.depthByFolderId, leaseFolderId],
      depthString: [...unitFolder.depthString, unitFolder.translatedLabel!],
      translatedLabel: lease.lease?.name,
      folderEntity: FolderEntity.LEASE,
      entityId: lease.leaseId,
      documentCategoryId: '',
      categoryType: FileCategory.CUSTOM,
      categoryEntity: EntityType.LEASE,
    };
    acc.push(leaseFolder);
    return acc;
  }, []);
};

export const getFolderToAddWithTargetFolder = (
  file: FileModel,
  targetFolder: FolderDatabaseModel,
  categoryType: FileCategory,
  invoice?: Invoice
) => {
  const fileEntityKey = `${targetFolder.entityId}-${file.id}`;
  const folderId = createDeterministicFolderId(FolderEntity.FILE, fileEntityKey, file.categoryId);
  const newFolder: FolderDatabaseModel = {
    id: folderId,
    entityId: targetFolder.entityId,
    folderEntity: FolderEntity.FILE,
    documentCategoryId: file.categoryId,
    categoryType,
    categoryEntity: targetFolder.categoryEntity,
    translatedLabel: decodeURIComponent(file.key.substring(file.key.indexOf('_') + 1)),
    documentId: file.id,
    mimeType: file.mimeType ?? '',
    size: file.size!,
    updatedAt: file.updatedAt,
    depth: `${targetFolder.depth}.${targetFolder.documentCategoryId}`,
    depthString: [...targetFolder.depthString, getFolderDepthStringProp(targetFolder)],
    depthByFolderId: [...targetFolder.depthByFolderId, folderId],
    isInvoicePaid: invoice?.paid ?? undefined,
  };

  return newFolder;
};

export const syncFile = (
  file: FileModel,
  fullDb: FolderDatabaseModel[],
  categories: DocumentCategoryWithoutReadIdAndClientId[],
  invoices: Invoice[],
  charges: ChargesMap[],
  technics: Technic[]
) => {
  const foldersToAdd: FolderDatabaseModel[] = [];
  if (isGeneratedUblDocument(file.mimeType)) {
    return foldersToAdd;
  }
  const documentFolder = fullDb.find((folder) => folder.documentId === file.id && folder.entityId === file.foreignKey);
  if (documentFolder) {
    return foldersToAdd;
  }
  if (!AllowedFilesEntities.includes(file.foreignTableName as EntityType)) {
    return;
  }
  const foldersInEntity = fullDb.filter((folder) => folder.entityId === file.foreignKey);

  const currentCategory = categories.find((category) => category.id === file.categoryId);
  if (!currentCategory) {
    return foldersToAdd;
  }

  const existingTargetFolders = foldersInEntity.filter((folderInEntity) => {
    return folderInEntity.documentCategoryId === file.categoryId && folderInEntity.folderEntity === FolderEntity.FOLDER;
  });
  if (!isNilOrEmpty(existingTargetFolders)) {
    for (const existingTargetFolder of existingTargetFolders) {
      foldersToAdd.push(getFolderToAddWithTargetFolder(file, existingTargetFolder, currentCategory.fileCategory));
    }
    return foldersToAdd;
  } else {
    if (currentCategory?.fileCategory === FileCategory.CHARGE_INVOICE) {
      const filteredCharges = charges.filter((charge) => charge.documentId === file.id);
      filteredCharges.forEach((charge) => {
        const currentEntityItems = fullDb.filter((folder) => folder.entityId === charge.entityId);
        const chargeFolders = calculateFoldersToAdd(currentCategory!, currentEntityItems, categories, file);
        foldersToAdd.push(...chargeFolders);
      });
      return foldersToAdd;
    }

    if (file.foreignTableName === EntityType.TECHNIC) {
      const currentTechnics = technics.filter(
        (technic) => technic.originalTechnicId === file.foreignKey || technic.id === file.foreignKey
      );
      currentTechnics.forEach((currentTechnic) => {
        const entityId = currentTechnic.leaseId ?? currentTechnic.unitId ?? currentTechnic.buildingId;
        const currentEntityItems = fullDb.filter((folder) => folder.entityId === entityId);
        const technicFolders = calculateFoldersToAdd(currentCategory, currentEntityItems, categories, file);
        foldersToAdd.push(...technicFolders);
      });
      return foldersToAdd;
    }

    if (currentCategory?.fileCategory === FileCategory.INVOICE) {
      const currentEntity = invoices.find((invoice) => invoice.id === file.foreignKey);
      if (!currentEntity || !currentEntity.leaseId) {
        return foldersToAdd;
      }
      const currentEntityItems = fullDb.filter((folder) => folder.entityId === currentEntity.leaseId);
      const invoiceFolders = calculateFoldersToAdd(
        currentCategory!,
        currentEntityItems,
        categories,
        file,
        currentEntity
      );
      foldersToAdd.push(...invoiceFolders);
      return foldersToAdd;
    }

    const currentEntityItems = fullDb.filter((folder) => folder.entityId === file.foreignKey);
    const fileFolders = calculateFoldersToAdd(currentCategory!, currentEntityItems, categories, file);
    foldersToAdd.push(...fileFolders);
    return foldersToAdd;
  }
};

export const calculateNewTargetFolder = (
  currentCategory: DocumentCategoryWithoutReadIdAndClientId,
  currentEntityItems: FolderDatabaseModel[],
  categories: DocumentCategoryWithoutReadIdAndClientId[]
) => {
  const foldersToAdd: FolderDatabaseModel[] = [];
  const { categoryDepth, folderCategories } = getCategoryDepthToAdd(
    currentCategory!,
    currentEntityItems,
    [],
    categories
  );

  const foldersToWorkWith = !isNilOrEmpty(folderCategories)
    ? folderCategories
    : currentEntityItems.filter(
        (entityFolder) =>
          ![FolderEntity.FOLDER, FolderEntity.FILE, FolderEntity.JOINT_CONTACT].includes(entityFolder.folderEntity)
      );

  for (const folderToWorkWithIndex in foldersToWorkWith) {
    let currentFolderToWorkWith = foldersToWorkWith[folderToWorkWithIndex];
    if (folderCategories) return { foldersToAdd, foldersToWorkWith };
    for (const category of categoryDepth) {
      const existingFolder = currentEntityItems.find(
        (folder) => folder.documentCategoryId === category.id && folder.folderEntity === FolderEntity.FOLDER
      );
      if (existingFolder) {
        foldersToWorkWith[folderToWorkWithIndex] = existingFolder;
        currentFolderToWorkWith = existingFolder;
        continue;
      }
      const folderId = createDeterministicFolderId(
        FolderEntity.FOLDER,
        currentFolderToWorkWith?.entityId ?? 'root',
        category.id
      );
      const newFolder: FolderDatabaseModel = {
        id: folderId,
        documentCategoryId: category.id,
        categoryType: currentCategory!.fileCategory,
        categoryEntity: currentCategory.entity ?? currentEntityItems[0]?.categoryEntity ?? '',
        depth: getFolderDepthForNavigation(currentFolderToWorkWith),
        depthString: [
          ...(currentFolderToWorkWith?.depthString || []),
          getFolderDepthStringProp(currentFolderToWorkWith),
        ],
        depthByFolderId: [...(currentFolderToWorkWith?.depthByFolderId ?? []), folderId],
        folderEntity: FolderEntity.FOLDER,
        labels: category.labels,
        updatedAt: fromDateTime(todayDateTime()),
        entityId: currentFolderToWorkWith?.entityId ?? '',
      };
      foldersToAdd.push(newFolder);
      foldersToWorkWith[folderToWorkWithIndex] = newFolder;
      currentFolderToWorkWith = newFolder;
    }
  }
  return { foldersToAdd, foldersToWorkWith };
};

export const calculateFoldersToAdd = (
  currentCategory: DocumentCategoryWithoutReadIdAndClientId,
  currentEntityItems: FolderDatabaseModel[],
  categories: DocumentCategoryWithoutReadIdAndClientId[],
  file: FileModel,
  invoice?: Invoice
) => {
  const foldersToAdd: FolderDatabaseModel[] = [];
  const { foldersToAdd: newFolders, foldersToWorkWith } = calculateNewTargetFolder(
    currentCategory,
    currentEntityItems,
    categories
  );
  foldersToAdd.push(...newFolders);
  for (const folder of foldersToWorkWith) {
    const newFolder = getFolderToAddWithTargetFolder(file, folder, currentCategory.fileCategory, invoice);
    foldersToAdd.push(newFolder);
  }
  return foldersToAdd;
};

const getCategoryDepthToAdd = (
  currentCategory: DocumentCategoryWithoutReadIdAndClientId,
  currentEntityFolders: FolderDatabaseModel[],
  categoryDepth: DocumentCategoryWithoutReadIdAndClientId[],
  categories: DocumentCategoryWithoutReadIdAndClientId[]
): { categoryDepth: DocumentCategoryWithoutReadIdAndClientId[]; folderCategories?: FolderDatabaseModel[] } => {
  const folderCategories = currentEntityFolders.filter(
    (folder) => folder.documentCategoryId === currentCategory.id && folder.folderEntity === FolderEntity.FOLDER
  );
  if (!isNilOrEmpty(folderCategories)) return { categoryDepth, folderCategories };

  if (currentCategory.parentId) {
    const newDepth = calculateCategoryDepth(currentCategory, categories, []);
    return { categoryDepth: newDepth };
  }
  return { categoryDepth: [...categoryDepth, currentCategory] };
};

const calculateCategoryDepth = (
  currentCategory: DocumentCategoryWithoutReadIdAndClientId,
  categories: DocumentCategoryWithoutReadIdAndClientId[],
  categoryDepth: DocumentCategoryWithoutReadIdAndClientId[]
): DocumentCategoryWithoutReadIdAndClientId[] => {
  const parentCategory = categories.find((category) => category.id === currentCategory.parentId);

  if (parentCategory) {
    return [...calculateCategoryDepth(parentCategory, categories, categoryDepth), currentCategory];
  }
  return [...categoryDepth, currentCategory];
};

const getFolderDepthForNavigation = (folder?: FolderDatabaseModel): string => {
  if (!folder) {
    return ROOT_FOLDER_ID;
  }
  if (folder.folderEntity === FolderEntity.FOLDER) {
    return `${folder.depth}.${folder.documentCategoryId}`;
  }
  return `${folder.depth}.${folder.entityId}`;
};

export const getFolderDepthStringProp = (folder: FolderDatabaseModel): string | CustomLabel[] => {
  if (folder.folderEntity === FolderEntity.FOLDER) {
    return folder.labels!;
  }
  return folder.translatedLabel!;
};
